/**
 * Normalize list-shaped API payloads. Angular HttpClient often exposes the JSON body directly;
 * Axios uses `response.data`. Backends may return a raw array, a wrapper object, a JSON string,
 * or a PHP-style numeric-key object instead of a JSON array.
 */
import { WRAPPER_KEYS } from './coerceApiArray.constants'

function pickBestArrayCandidate(arrays: unknown[][]): unknown[] {
  if (arrays.length === 0) return []
  if (arrays.length === 1) return arrays[0]
  const nonEmpty = arrays.filter((a) => a.length > 0)
  if (nonEmpty.length === 1) return nonEmpty[0]
  if (nonEmpty.length > 1) {
    return nonEmpty.reduce((a, b) => (b.length > a.length ? b : a))
  }
  return arrays[0]
}

function collectArraysFromValues(o: Record<string, unknown>): unknown[][] {
  const out: unknown[][] = []
  for (const v of Object.values(o)) {
    if (Array.isArray(v)) out.push(v)
  }
  return out
}

export function coerceApiArray<T = unknown>(raw: unknown): T[] {
  if (raw == null) return []
  if (Array.isArray(raw)) return raw as T[]
  if (typeof raw === 'string') {
    const t = raw.trim()
    if ((t.startsWith('[') && t.endsWith(']')) || (t.startsWith('{') && t.endsWith('}'))) {
      try {
        return coerceApiArray(JSON.parse(t) as unknown)
      } catch {
        return []
      }
    }
    return []
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>
    for (const key of WRAPPER_KEYS) {
      if (!(key in o)) continue
      const v = o[key]
      if (Array.isArray(v)) return v as T[]
      if (typeof v === 'string') {
        const nested = coerceApiArray<T>(v)
        if (nested.length > 0) return nested
        continue
      }
      if (v != null && typeof v === 'object' && !Array.isArray(v)) {
        const nested = coerceApiArray<T>(v)
        if (nested.length > 0) return nested
      }
    }
    const keys = Object.keys(o)
    if (keys.length > 0 && keys.every((k) => /^\d+$/.test(k))) {
      return [...keys]
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => o[k]) as T[]
    }

    const directArrays = collectArraysFromValues(o)
    if (directArrays.length > 0) {
      return pickBestArrayCandidate(directArrays) as T[]
    }
  }
  return []
}
